import React, { useMemo } from 'react';
import './PaginationBar.css';

function clamp(n, min, max) {
  if (Number.isNaN(n)) return min;
  return Math.max(min, Math.min(max, n));
}

export default function PaginationBar({
  page,
  pageSize,
  total,
  onPageChange,
  className = '',
  ariaLabel = 'Pagination',
}) {
  const safeTotal = Number.isFinite(total) && total > 0 ? total : 0;
  const safePageSize = Number.isFinite(pageSize) && pageSize > 0 ? pageSize : 10;

  const pageCount = Math.max(1, Math.ceil(safeTotal / safePageSize));
  const safePage = clamp(Number(page) || 0, 0, pageCount - 1);

  const from = safeTotal === 0 ? 0 : safePage * safePageSize + 1;
  const to = safeTotal === 0 ? 0 : Math.min(safeTotal, (safePage + 1) * safePageSize);

  const pages = useMemo(() => {
    // Compact page list: first, last, current +/- 1, with ellipses
    if (pageCount <= 7) return Array.from({ length: pageCount }, (_, i) => i);

    const set = new Set([0, pageCount - 1, safePage - 1, safePage, safePage + 1]);
    const nums = Array.from(set).filter((n) => n >= 0 && n <= pageCount - 1).sort((a, b) => a - b);

    const out = [];
    for (let i = 0; i < nums.length; i += 1) {
      const cur = nums[i];
      const prev = nums[i - 1];
      if (i > 0 && cur - prev > 1) out.push('…');
      out.push(cur);
    }
    return out;
  }, [pageCount, safePage]);

  const goTo = (next) => {
    const n = clamp(next, 0, pageCount - 1);
    if (n === safePage) return;
    onPageChange?.(n);
  };

  return (
    <nav className={`pgbar ${className}`.trim()} aria-label={ariaLabel}>
      <div className="pgbar__meta">
        Showing <strong>{from}</strong>–<strong>{to}</strong> of <strong>{safeTotal}</strong>
      </div>

      <div className="pgbar__controls">
        <button
          type="button"
          className="pgbar__btn"
          onClick={() => goTo(safePage - 1)}
          disabled={safePage <= 0}
        >
          Prev
        </button>

        <div className="pgbar__pages" role="list">
          {pages.map((p, idx) =>
            p === '…' ? (
              <span key={`ellipsis-${idx}`} className="pgbar__ellipsis" aria-hidden="true">
                …
              </span>
            ) : (
              <button
                key={p}
                type="button"
                className={`pgbar__page ${p === safePage ? 'pgbar__page--active' : ''}`.trim()}
                onClick={() => goTo(p)}
                aria-current={p === safePage ? 'page' : undefined}
              >
                {p + 1}
              </button>
            )
          )}
        </div>

        <button
          type="button"
          className="pgbar__btn"
          onClick={() => goTo(safePage + 1)}
          disabled={safePage >= pageCount - 1}
        >
          Next
        </button>
      </div>
    </nav>
  );
}

